import { useState, useRef } from 'react';
import Dialog from '@raydiant/raydial/components/Dialog';
import Button from '@raydiant/raydial/components/Button';
import Popover, { PopoverProps } from '@raydiant/raydial/components/Popover';
import {
  CircleArrowRight,
  CircleNodes,
  Edit,
  Ellipsis,
  Trash,
} from '@raydiant/raydial/components/Icons';
import ContentCardActionButton from './ContentCardActionButton';
import { Content } from '@raydiant/api-client-js';
import { useSelector } from 'react-redux';
import { selectUserProfile } from '../../../../../selectors/user';
import {
  canDeleteContent,
  canEditContent,
  canShareContent,
} from '../../../../../utilities';
import useCopyContent from '../../../../../hooks/useCopyContent';
import { createDuplicateContentName } from '../../../../../utilities/getDuplicateResourceName';
import { DEFAULT_QUERY } from '../../../../AddContentPage/AddContentPageV2';
import { useLibrary } from '../../../../../hooks';
import { useToastState } from '@raydiant/raydial/components/Toast';
import { capitalize } from '../../../../../utilities/stringUtils';
import { LibraryPageModalType } from '../../LibraryPageV2';
import DuplicateMultiple from 'raydiant-elements/icons/Duplicate';

interface ContentCardActionButtonGroupProps {
  content: Content;
  placement?: PopoverProps['placement'];
  onButtonGroupClick: (contentId: string) => void;
  onButtonGroupItemClick: (type: LibraryPageModalType) => void;
  onButtonGroupClose: () => void;
}

const ContentCardActionButtonGroup = ({
  content,
  placement = 'bottom start',
  onButtonGroupClick,
  onButtonGroupItemClick,
  onButtonGroupClose,
}: ContentCardActionButtonGroupProps) => {
  const state = useToastState();

  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef(null);

  const currentUser = useSelector(selectUserProfile);
  const { data: libraryData } = useLibrary(DEFAULT_QUERY);
  const { mutateAsync: copyContent } = useCopyContent();

  const handleIconClick = () => {
    setIsOpen(true);
    onButtonGroupClick(content.resourceId);
  };

  const handleItemClick = (modalType: LibraryPageModalType) => {
    setIsOpen(false);
    onButtonGroupItemClick(modalType);
  };

  const handleDuplicateContent = async () => {
    try {
      await copyContent({
        content: content,
        params: {
          name: createDuplicateContentName(
            content.name,
            libraryData?.data || [],
          ),
        },
      });

      state.add({
        title: `${capitalize(content.resourceType)} duplicated`,
        description: `${capitalize(
          content.resourceType,
        )} duplicated successfully`,
      });
    } catch (error) {
      state.add({
        title: 'Something went wrong',
        description: 'Please try again',
        variant: 'caution',
      });
    }
  };

  const isFolder = content.resourceType === 'folder';
  const canShare = currentUser && canShareContent(currentUser, content);
  const canEdit = currentUser && canEditContent(currentUser, content);
  const canDelete = currentUser && canDeleteContent(currentUser, content);

  if (!currentUser) return null;
  return (
    <>
      <Button
        variant="unstyled"
        className="bg-white w-6 h-6 rounded-xl flex items-center justify-center pl-[0.5px]"
        onPress={() => handleIconClick()}
      >
        <span ref={triggerRef} className="rotate-90 mb-0.5">
          <Ellipsis />
        </span>
      </Button>
      <Popover
        triggerRef={triggerRef}
        isOpen={isOpen}
        onOpenChange={setIsOpen}
        className="w-[166px]"
        placement={placement}
      >
        <Dialog className="px-0 py-2" aria-label="manage content">
          <div className="overflow-hidden">
            <ContentCardActionButton
              icon={<CircleNodes />}
              label="Share"
              onClick={() => handleItemClick('share')}
              isDisabled={!canShare}
            />

            {
              <ContentCardActionButton
                icon={<Edit />}
                label="Rename"
                onClick={() => handleItemClick('rename')}
                isDisabled={!canEdit}
              />
            }

            {!isFolder && (
              <ContentCardActionButton
                icon={<DuplicateMultiple />} // Change this to icon after add into the raydial
                label="Duplicate"
                onClick={() => handleDuplicateContent()}
              />
            )}

            {
              <ContentCardActionButton
                icon={<CircleArrowRight />}
                label="Move"
                onClick={() => handleItemClick('move')}
                isDisabled={content.ownedBy.id !== currentUser.id}
              />
            }

            <ContentCardActionButton
              className="text-red-700"
              icon={<Trash />}
              label="Delete"
              onClick={() => handleItemClick('delete')}
              isDisabled={!canDelete}
            />
          </div>
        </Dialog>
      </Popover>
    </>
  );
};

export default ContentCardActionButtonGroup;
